import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Table } from '../components/ui/Table.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { formatDate } from '../utils/formatters.js';

import { NewTenantModal } from './NewTenantModal.jsx';
import { usePlatformAuth } from './PlatformAuthContext.jsx';
import { tenantsApi } from './platformApi.js';
import './platform.css';

const STATUS_VARIANT = {
  active: 'success',
  provisioning: 'warning',
  blocked: 'danger',
  failed: 'danger',
  deleting: 'warning',
  deleted: 'neutral',
};

export function TenantsPage() {
  const { t } = useTranslation();
  const { permissions } = usePlatformAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);

  const canWrite = permissions?.tenants === 'write';

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'tenants', search, status],
    queryFn: () => tenantsApi.list({ search, status }),
  });

  const rows = data?.items || [];

  const toggleBlock = useMutation({
    // Eigener Endpunkt: das Sperren beendet zugleich jede laufende Sitzung.
    mutationFn: ({ id, next }) => tenantsApi.setStatus(id, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });

  const statusLabel = (key) => t(`platform.tenants.status.${key}`);

  const columns = [
    { key: 'slug', label: t('platform.tenants.col.slug'), render: (r) => <span className="mono">{r.slug}</span> },
    { key: 'name', label: t('platform.tenants.col.name') },
    { key: 'contactName', label: t('platform.tenants.col.contact'), render: (r) => r.contactName || '-' },
    { key: 'contactEmail', label: t('common.email'), render: (r) => r.contactEmail || '-' },
    {
      key: 'status',
      label: t('common.status'),
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{statusLabel(r.status)}</Badge>,
    },
    { key: 'createdAt', label: t('platform.tenants.col.created'), render: (r) => formatDate(r.createdAt) },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => {
        if (!canWrite || r.status === 'provisioning' || r.status === 'failed') {
          return null;
        }
        const blocked = r.status === 'blocked';
        return (
          <Button
            size="sm"
            variant="ghost"
            disabled={toggleBlock.isPending}
            onClick={() => toggleBlock.mutate({ id: r.id, next: blocked ? 'active' : 'blocked' })}
          >
            {blocked ? t('platform.tenants.unblock') : t('platform.tenants.block')}
          </Button>
        );
      },
    },
  ];

  const anyProvisioning = rows.some((r) => r.status === 'provisioning');

  return (
    <>
      <div className="pf-page-head">
        <div>
          <h1>{t('platform.tenants.title')}</h1>
          <p>{t('platform.tenants.subtitle')}</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t('platform.tenants.create.cta')}
          </Button>
        )}
      </div>

      {anyProvisioning && <div className="pf-note">{t('platform.tenants.provisioningNote')}</div>}

      <div className="pf-filters">
        <input
          className="input"
          style={{ maxWidth: 280 }}
          placeholder={t('platform.tenants.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input" style={{ maxWidth: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('platform.tenants.status.all')}</option>
          <option value="active">{statusLabel('active')}</option>
          <option value="provisioning">{statusLabel('provisioning')}</option>
          <option value="blocked">{statusLabel('blocked')}</option>
          <option value="failed">{statusLabel('failed')}</option>
        </select>
        <span className="muted text-xs">
          {t('platform.tenants.count', { count: data?.meta?.total ?? rows.length })}
        </span>
      </div>

      <Table
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        emptyMessage={t('platform.tenants.empty')}
        onRowClick={(row) => navigate(`/platform/tenants/${row.id}`)}
      />

      <NewTenantModal open={creating} onClose={() => setCreating(false)} />
    </>
  );
}
